import posthog, { type PostHogConfig } from 'posthog-js';
import { LABS_EXCEPTION_CAPTURE } from '../../../../shared/exception-scrubber';
import { prepareBrowserEvent } from './privacy';
import type { AnalyticsEvent } from '../../core/events';

/**
 * Browser analytics, copied from Planner without its subscription enrichment (Profile has no
 * wall). Session recording is off: a profile page is a teacher's name, photo and history.
 */
export interface Analytics {
  track(event: AnalyticsEvent): void;
  dispose(): void;
}
export const nullAnalytics = (): Analytics => ({ track() {}, dispose() {} });
const quietly = (fn: () => void) => { try { fn(); } catch { /* Measurement must never interrupt the page. */ } };
const interactions: Partial<PostHogConfig> = {
  capture_pageview: 'history_change', capture_pageleave: true, capture_heatmaps: true,
  autocapture: { dom_event_allowlist: ['click'], capture_copied_text: false },
};
export function createAnalytics(userId: string, email: string | null): Analytics {
  if (!import.meta.env.VITE_POSTHOG_KEY) return nullAnalytics();
  let active = true;
  quietly(() => {
    posthog.init(import.meta.env.VITE_POSTHOG_KEY!, {
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://s.240.org',
      ui_host: 'https://us.posthog.com',
      defaults: '2026-05-30',
      ...interactions, person_profiles: 'identified_only',
      mask_all_text: true, mask_all_element_attributes: true,
      disable_surveys: true, disable_session_recording: true,
      capture_dead_clicks: false, capture_performance: false,
      // Errors are on, scrubbed first. See shared/exception-scrubber.ts.
      capture_exceptions: LABS_EXCEPTION_CAPTURE, before_send: prepareBrowserEvent,
    });
    // Identified by email, so a colleague testing the tool can be told apart from a teacher.
    posthog.identify(userId, email ? { email } : {});
    // init is a no-op on account changes; restore capture stopped by dispose.
    posthog.set_config(interactions);
  });
  return {
    // `prepareBrowserEvent` stamps product and event_source on every capture, this one included.
    track(event) {
      if (!active) return;
      quietly(() => posthog.capture(event.name, { ...event.properties, ...(email ? { $set: { email } } : {}) }));
    },
    dispose() {
      active = false;
      quietly(() => { posthog.set_config({ autocapture: false, capture_heatmaps: false, capture_pageview: false, capture_pageleave: false }); });
      quietly(() => { posthog.reset(); });
    },
  };
}
export function posthogHeaders(): Record<string, string> {
  if (!import.meta.env.VITE_POSTHOG_KEY) return {};
  try {
    const id = posthog.get_distinct_id(); const session = posthog.get_session_id();
    return id && session ? { 'X-POSTHOG-DISTINCT-ID': id, 'X-POSTHOG-SESSION-ID': session } : {};
  } catch { return {}; }
}
